"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, useQuery } from "@opensea/graphql";
import { Combobox, ComboboxEmpty, ComboboxGroup, ComboboxItem, ComboboxItemContent, ComboboxItemSide, ComboboxItemTitle, ComboboxList, FlexCenter, Input, NumberDisplay, Separator, SpaceBetween, TextBody, TextBodySkeleton, TextLabel, TextOverflowTooltip } from "@opensea/ui-kit";
import { useDebounceValue } from "@opensea/ui-kit/hooks";
import { groupBy } from "es-toolkit/array";
import { range } from "es-toolkit/math";
import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import { TokenPrice } from "@/components/price/TokenPrice";
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment";
import { withSuspense } from "@/components/suspense/WithSuspense";
import { useSearchQueryQueryParam } from "@/hooks/query-param/use-search-query-query-param";
import { useTraitsQueryParam } from "@/hooks/query-param/use-traits-query-param";
import { CollectionItemSearchItem } from "./CollectionItemSearchItem";
import { CollectionItemSearchItemFragment } from "./CollectionItemSearchItem.fragment";
const DEBOUNCE_DELAY = 200;
const traitQuery = graphql(`
    query TraitSearchInputQuery($collectionSlug: String!) {
      collectionAttributesBySlug(
        slug: $collectionSlug
        limit: 2000
        sort: { by: TRAIT_COUNT, direction: DESC }
      ) {
        items {
          traitType
          count
          traitValue
          floorPrice {
            pricePerItem {
              ...TokenPrice
            }
          }
          percent
        }
      }
    }
  `, [TokenPriceFragment]);
const itemQuery = graphql(`
    query ItemsByQuery($collectionSlug: String!, $query: String!) {
      itemsByQuery(collectionSlug: $collectionSlug, query: $query, limit: 5) {
        id
        ...CollectionItemSearchItem
      }
    }
  `, [CollectionItemSearchItemFragment]);
function TraitOrItemSearchInputFallback() {
  const $ = _c(4);
  const t = useTranslations("TraitOrItemSearchInput");
  let t0;
  if ($[0] !== t) {
    t0 = t("searchPlaceholder");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <Input placeholder={t0} size="sm" />;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
export const TraitOrItemSearchInput = withSuspense(({
  collectionSlug,
  className
}) => {
  const t = useTranslations("TraitOrItemSearchInput");
  const [searchQuery, setSearchQuery] = useState("");
  const {
    setSearchQuery: setSearchQueryQueryParam
  } = useSearchQueryQueryParam();
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);
  const [debouncedSearchQuery] = useDebounceValue(searchQuery.trim(), DEBOUNCE_DELAY);
  useEffect(() => {
    if (searchQuery) {
      setIsComboboxOpen(true);
    }
  }, [searchQuery, setIsComboboxOpen]);
  return <Combobox className={className} content={<ComboboxList>
            <ComboboxGroup heading={t("traits")}>
              <TraitSearchList collectionSlug={collectionSlug} onSelect={() => {
        setIsComboboxOpen(false);
        setSearchQuery("");
      }} searchQuery={searchQuery} />
            </ComboboxGroup>

            {debouncedSearchQuery ? <>
                <Separator className="my-2" />
                <ComboboxGroup heading={t("items")}>
                  <ItemSearchList collectionSlug={collectionSlug} onSelect={() => {
          setIsComboboxOpen(false);
          setSearchQuery("");
        }} searchQuery={debouncedSearchQuery} />
                </ComboboxGroup>
              </> : null}
          </ComboboxList>} onInteractOutside={() => setIsComboboxOpen(false)} onKeyDown={async e => {
    if (e.key === "Enter" && searchQuery) {
      e.preventDefault();
      setIsComboboxOpen(false);
      setSearchQuery("");
      await setSearchQueryQueryParam(searchQuery);
    }
  }} onValueChange={setSearchQuery} open={isComboboxOpen} placeholder={t("searchPlaceholder")} shouldFilter={false} size="sm" value={searchQuery} />;
}, {
  fallback: TraitOrItemSearchInputFallback
});
const TraitSearchList = withSuspense(({
  searchQuery,
  collectionSlug,
  onSelect
}) => {
  const t = useTranslations("TraitOrItemSearchInput");
  const {
    onSelectTrait
  } = useTraitsQueryParam();
  const [{
    data,
    error
  }] = useQuery({
    query: traitQuery,
    variables: {
      collectionSlug
    }
  }, {
    throwOnError: false
  });
  const filteredItems = (data?.collectionAttributesBySlug.items ?? []).filter(({
    traitType,
    traitValue
  }) => traitValue.toLowerCase().includes(searchQuery.toLowerCase()) || traitType.toLowerCase().includes(searchQuery.toLowerCase()));
  const groupedItems = Object.fromEntries(Object.entries(groupBy(filteredItems, v => v.traitType)).sort(([a], [b]) => a.localeCompare(b)));
  return <>
        <ComboboxEmpty>
          {error ? "Something went wrong. Please try again later" : "No results found"}
        </ComboboxEmpty>
        {Object.entries(groupedItems).map(([traitType, items], index, a) => {
      if (!items) {
        return null;
      }
      return <React.Fragment key={traitType}>
              <SpaceBetween className="p-2">
                <TextLabel size="2xs">{traitType}</TextLabel>
                <TextLabel size="2xs">{t("floor")}</TextLabel>
              </SpaceBetween>

              {items.map(({
          traitValue,
          floorPrice,
          count,
          percent
        }) => {
          return <ComboboxItem key={`${traitType}-${traitValue}`} onSelect={() => {
            onSelect();
            void onSelectTrait(traitType, traitValue);
          }} value={`${traitType} ${traitValue}`}>
                    <FlexCenter className="w-full gap-2">
                      <TextOverflowTooltip>
                        <TextBody className="mr-1">{traitValue}</TextBody>
                      </TextOverflowTooltip>

                      <TextLabel>
                        <NumberDisplay display="quantity" value={count} />
                      </TextLabel>

                      <TextLabel color="text-secondary">
                        <NumberDisplay custom={{
                  maximumSignificantDigits: 2
                }} display="percent" value={percent} />
                      </TextLabel>
                    </FlexCenter>
                    <ComboboxItemSide>
                      <TextLabel>
                        <TokenPrice price={floorPrice?.pricePerItem} symbolColor="current" />
                      </TextLabel>
                    </ComboboxItemSide>
                  </ComboboxItem>;
        })}

              {index < a.length - 1 && <Separator className="my-2" />}
            </React.Fragment>;
    })}
      </>;
}, {
  fallback: <TraitSearchGroupSkeleton />
});
function TraitSearchGroupSkeleton() {
  return range(3).map(i => {
    return <ComboboxItem key={i}>
        <ComboboxItemContent>
          <div className="flex w-full truncate">
            <TextBodySkeleton className="mr-4 w-12" />
            <ComboboxItemTitle className="w-full">
              <TextBodySkeleton />
            </ComboboxItemTitle>
          </div>
        </ComboboxItemContent>
      </ComboboxItem>;
  });
}
const ItemSearchList = withSuspense(({
  searchQuery,
  collectionSlug,
  onSelect
}) => {
  const [{
    data: dataItem,
    error: errorItem
  }] = useQuery({
    query: itemQuery,
    variables: {
      collectionSlug,
      query: searchQuery
    }
  }, {
    throwOnError: false
  });
  const t = useTranslations("TraitOrItemSearchInput");
  return <>
        <ComboboxEmpty>
          {errorItem ? t("somethingWentWrong") : t("noResultsFound")}
        </ComboboxEmpty>
        {dataItem?.itemsByQuery.map(item => <CollectionItemSearchItem item={item} key={item.id} onSelect={onSelect} />)}
      </>;
}, {
  fallback: <ItemComboboxGroupSkeleton />
});
function ItemComboboxGroupSkeleton() {
  return range(3).map(i => {
    return <ComboboxItem key={i}>
        <ComboboxItemContent>
          <div className="flex w-full truncate">
            <TextBodySkeleton className="mr-4 w-12" />
            <ComboboxItemTitle className="w-full">
              <TextBodySkeleton />
            </ComboboxItemTitle>
          </div>
        </ComboboxItemContent>
      </ComboboxItem>;
  });
}